import { Button, MantineThemeProvider, Modal, Table } from "@mantine/core";
import type { ButtonCssVariables, MantineThemeOverride, ModalProps } from "@mantine/core";
import { modals } from "@mantine/modals";
import * as React from "react";
import FifoWarningModal from "../../WarehouseOutWard/components/FifoWarningModal";
import type { SaleOutwardError } from "../types";

// --- Khung hộp thoại dùng chung theo thiết kế (LockBlocked / DevicePicker) ---


/** Bo 16, không thanh tiêu đề mặc định, nền mờ tối, đổ bóng như thiết kế */
export const DIALOG_MODAL_PROPS: Pick<ModalProps, "radius" | "padding" | "withCloseButton" | "overlayProps" | "styles"> = {
  radius: 16,
  padding: 0,
  withCloseButton: false,
  overlayProps: { color: "#161A1E", backgroundOpacity: 0.48 },
  styles: {
    content: { boxShadow: "0 24px 64px rgba(0, 0, 0, 0.28)", color: "#1B1F24", fontSize: 14 },
  },
};

type ButtonVars = Partial<Record<ButtonCssVariables["root"], string>>;

const BUTTON_SIZE: ButtonVars = { "--button-height": "44px", "--button-radius": "10px", "--button-fz": "14px" };

const PRIMARY_BUTTON: ButtonVars = {
  ...BUTTON_SIZE,
  "--button-padding-x": "20px",
  "--button-bg": "#1F5FAE",
  "--button-hover": "#174A88",
  "--button-color": "#FFFFFF",
  "--button-hover-color": "#FFFFFF",
  "--button-bd": "1px solid transparent",
};

const SECONDARY_BUTTON: ButtonVars = {
  ...BUTTON_SIZE,
  "--button-padding-x": "18px",
  "--button-bg": "#FFFFFF",
  "--button-hover": "#F6F7F4",
  "--button-color": "#1B1F24",
  "--button-hover-color": "#1B1F24",
  "--button-bd": "1px solid #D5D9D2",
};

/** Nút dạng liên kết ("Tới dòng đơn", "Chọn thiết bị") */
const LINK_BUTTON: ButtonVars = {
  "--button-height": "auto",
  "--button-padding-x": "0px",
  "--button-fz": "14px",
  "--button-bg": "transparent",
  "--button-hover": "transparent",
  "--button-color": "#1F5FAE",
  "--button-hover-color": "#174A88",
  "--button-bd": "none",
};

interface DialogButtonProps {
  tone?: "primary" | "secondary";
  disabled?: boolean;
  /** Nhận focus khi hộp thoại mở */
  autoFocus?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}

/** Nút chân hộp thoại: cao 44, bo 10; nút chính nền #1F5FAE, nút phụ viền xám */
export const DialogButton = ({ tone = "primary", disabled, autoFocus, onClick, children }: DialogButtonProps) => (
  <Button
    variant="filled"
    fw={600}
    disabled={disabled}
    data-autofocus={autoFocus || undefined}
    onClick={onClick}
    vars={() => ({ root: tone === "primary" ? PRIMARY_BUTTON : SECONDARY_BUTTON })}
  >
    {children}
  </Button>
);

const TITLE_STYLE: React.CSSProperties = {
  margin: 0,
  fontSize: 19,
  fontWeight: 700,
  lineHeight: 1.3,
  color: "#1B1F24",
  overflowWrap: "anywhere",
};

interface DialogHeaderProps {
  icon: React.ReactNode;
  /** Nền ô icon 44px */
  iconBackground: string;
  title: React.ReactNode;
  description?: React.ReactNode;
  /** Có nút đóng thì đặt ở góc phải và kẻ dưới đầu hộp (hộp chọn thiết bị) */
  closeButton?: React.ReactNode;
}

/** Đầu hộp thoại: ô icon, tiêu đề 19px đậm (Modal.Title để hộp có aria-labelledby), mô tả màu phụ */
export const DialogHeader = ({ icon, iconBackground, title, description, closeButton }: DialogHeaderProps) => (
  <div
    style={{
      display: "flex",
      alignItems: "flex-start",
      gap: 14,
      padding: closeButton ? "24px 24px 18px" : "24px 24px 16px",
      borderBottom: closeButton ? "1px solid #ECEEE9" : undefined,
    }}
  >
    <div
      style={{
        width: 44,
        height: 44,
        flexShrink: 0,
        borderRadius: 12,
        background: iconBackground,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {icon}
    </div>
    <div style={{ flexGrow: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 4 }}>
      <Modal.Title style={TITLE_STYLE}>{title}</Modal.Title>
      {description && <p style={{ margin: 0, color: "#5C6570", lineHeight: 1.5 }}>{description}</p>}
    </div>
    {closeButton}
  </div>
);

const DialogFooter = ({ children }: { children: React.ReactNode }) => (
  <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, padding: "20px 24px 24px" }}>{children}</div>
);

const LockIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#8A4B05"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="5" y="11" width="14" height="10" rx="2" />
    <path d="M8 11V7a4 4 0 0 1 8 0v4" />
  </svg>
);

const AlertIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#8A4B05"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 4 2.5 20h19z" />
    <path d="M12 10v4M12 17h.01" />
  </svg>
);

const BLOCKING_MODAL_ID = "sale-outward-blocking-errors";
const VARIANCE_MODAL_ID = "sale-outward-confirm-variance";

const FIFO_MODAL_ID = "sale-outward-confirm-fifo";

// --- Hộp lỗi chặn lưu / hoàn thành ---

/** Nhãn nút đi tới chỗ cần xử lý; null nếu vướng mắc không gắn với dòng nào */
const goToLabel = (error: SaleOutwardError) => {
  if (error.code === "CARD_NOT_LINKED") return "Chọn thiết bị";
  if (error.itemCode) return "Tới dòng đơn";
  if (error.serial) return "Tới dòng";
  return null;
};

interface BlockingErrorsProps {
  title: string;
  errors: SaleOutwardError[];
  onGoTo?: (error: SaleOutwardError) => void;
  onClose: () => void;
}

const BlockingErrors = ({ title, errors, onGoTo, onClose }: BlockingErrorsProps) => {
  return (
    <div>
      <DialogHeader icon={<LockIcon />} iconBackground="#FFF1DC" title={title} />
      <ol style={{ listStyle: "none", margin: 0, padding: "0 24px", display: "flex", flexDirection: "column", gap: 10 }}>
        {errors.map((error, index) => {
          const label = onGoTo ? goToLabel(error) : null;
          return (
            <li
              key={`${error.code}-${index}`}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: 14,
                border: "1px solid #F1D9B5",
                background: "#FFF9EF",
                borderRadius: 12,
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  width: 28,
                  height: 28,
                  flexShrink: 0,
                  borderRadius: 999,
                  background: "#FFFFFF",
                  border: "1px solid #F1D9B5",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 700,
                  color: "#8A4B05",
                }}
              >
                {index + 1}
              </span>
              <div
                style={{
                  flexGrow: 1,
                  minWidth: 0,
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  overflowWrap: "anywhere",
                }}
              >
                <span style={{ fontWeight: 600 }}>{error.message}</span>
                {error.detail && <span style={{ color: "#5C6570", fontSize: 13 }}>{error.detail}</span>}
              </div>
              {label && onGoTo && (
                <Button
                  variant="subtle"
                  fw={600}
                  style={{ flexShrink: 0 }}
                  vars={() => ({ root: LINK_BUTTON })}
                  onClick={() => onGoTo(error)}
                >
                  {label}
                </Button>
              )}
            </li>
          );
        })}
      </ol>
      <DialogFooter>
        <DialogButton autoFocus onClick={onClose}>
          Đã hiểu
        </DialogButton>
      </DialogFooter>
    </div>
  );
};

/**
 * Liệt kê toàn bộ lỗi chặn lưu / hoàn thành trong một hộp thoại.
 * Có `onGoTo` thì vướng mắc gắn với dòng có nút đi tới dòng đó: đóng hộp rồi gọi `onGoTo`.
 */
export const showBlockingErrors = (
  title: string,
  errors: SaleOutwardError[],
  onGoTo?: (error: SaleOutwardError) => void,
) => {
  const close = () => modals.close(BLOCKING_MODAL_ID);
  modals.open({
    modalId: BLOCKING_MODAL_ID,
    ...DIALOG_MODAL_PROPS,
    size: 560,
    yOffset: "20dvh",
    children: (
      <BlockingErrors
        title={title}
        errors={errors}
        onClose={close}
        onGoTo={
          onGoTo
            ? (error) => {
                close();
                onGoTo(error);
              }
            : undefined
        }
      />
    ),
  });
};

// --- Xác nhận lệch số lượng ---

const TH_STYLE: React.CSSProperties = {
  position: "sticky",
  top: 0,
  padding: "8px 12px",
  background: "#F6F7F4",
  borderBottom: "1px solid #ECEEE9",
  color: "#5C6570",
  fontSize: 12,
  fontWeight: 600,
  textAlign: "left",
};

const TD_STYLE: React.CSSProperties = { padding: "9px 12px", fontSize: 12.5 };

/** Hỏi xác nhận khi còn sản phẩm lệch số lượng. Trả về true nếu người dùng xác nhận */
export const confirmVariance = (
  items: { itemName: string; varianceQuantity: number }[],
  isLock: boolean,
): Promise<boolean> =>
  new Promise((resolve) => {
    let answered = false;
    const answer = (value: boolean) => {
      answered = true;
      modals.close(VARIANCE_MODAL_ID);
      resolve(value);
    };
    modals.open({
      modalId: VARIANCE_MODAL_ID,
      ...DIALOG_MODAL_PROPS,
      size: 560,
      yOffset: "20dvh",
      onClose: () => {
        if (!answered) resolve(false);
      },
      children: (
        <div>
          <DialogHeader
            icon={<AlertIcon />}
            iconBackground="#FFF1DC"
            title="Có những sản phẩm sau bị lệch"
            description={`Bạn có chắc chắn ${isLock ? "hoàn thành" : "lưu"} phiếu?`}
          />
          <div
            style={{ margin: "0 24px", border: "1px solid #DDE0DA", borderRadius: 12, overflow: "auto", maxHeight: 360 }}
          >
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={TH_STYLE}>Tên sản phẩm</th>
                  <th style={{ ...TH_STYLE, textAlign: "center", width: 130 }}>Số lượng lệch</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, index) => {
                  const rowBorder = index > 0 ? "1px solid #F0F1EE" : undefined;
                  return (
                    <tr key={index}>
                      <td style={{ ...TD_STYLE, borderTop: rowBorder, fontWeight: 600 }}>{item.itemName}</td>
                      <td
                        style={{
                          ...TD_STYLE,
                          borderTop: rowBorder,
                          textAlign: "center",
                          fontWeight: 700,
                          color: "#B42318",
                        }}
                      >
                        {item.varianceQuantity}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <DialogFooter>
            <DialogButton tone="secondary" onClick={() => answer(false)}>
              Huỷ
            </DialogButton>
            <DialogButton autoFocus onClick={() => answer(true)}>
              Xác nhận
            </DialogButton>
          </DialogFooter>
        </div>
      ),
    });
  });

// --- Xuất đổi trả: còn serial xuất chưa chọn "Serial nhập" ---

const EXCHANGE_MISSING_MODAL_ID = "sale-outward-confirm-exchange-missing";

/**
 * Đơn có nhiều serial trả mà còn serial xuất chưa chọn "Serial nhập": hỏi trước khi hoàn thành, không chặn
 * (quyết định 13/10/2026); backend sẽ ghép serial đó với mọi serial trả của đơn, không biết chính xác từng cặp.
 * Trả về true nếu người dùng vẫn hoàn thành.
 */
export const confirmMissingExchangeSerials = (serials: string[]): Promise<boolean> =>
  new Promise((resolve) => {
    let answered = false;
    const answer = (value: boolean) => {
      answered = true;
      modals.close(EXCHANGE_MISSING_MODAL_ID);
      resolve(value);
    };
    modals.open({
      modalId: EXCHANGE_MISSING_MODAL_ID,
      ...DIALOG_MODAL_PROPS,
      size: 560,
      yOffset: "20dvh",
      onClose: () => {
        if (!answered) resolve(false);
      },
      children: (
        <div>
          <DialogHeader
            icon={<AlertIcon />}
            iconBackground="#FFF1DC"
            title="Còn serial xuất chưa chọn serial nhập"
            description="Các serial dưới đây sẽ được ghép với mọi serial khách trả của đơn, không biết chính xác máy nào đổi cho máy nào. Vẫn hoàn thành phiếu?"
          />
          <div
            style={{ margin: "0 24px", border: "1px solid #DDE0DA", borderRadius: 12, overflow: "auto", maxHeight: 360 }}
          >
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={TH_STYLE}>Serial xuất</th>
                </tr>
              </thead>
              <tbody>
                {serials.map((serial, index) => (
                  <tr key={serial}>
                    <td style={{ ...TD_STYLE, borderTop: index > 0 ? "1px solid #F0F1EE" : undefined, fontWeight: 600 }}>
                      {serial}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <DialogFooter>
            <DialogButton tone="secondary" onClick={() => answer(false)}>
              Quay lại chọn
            </DialogButton>
            <DialogButton autoFocus onClick={() => answer(true)}>
              Vẫn hoàn thành
            </DialogButton>
          </DialogFooter>
        </div>
      ),
    });
  });

// --- Cảnh báo FIFO ---

/**
 * FifoWarningModal là component cũ (không sửa) nên đổi kiểu bên trong qua theme:
 * nút "Quay lại cân nhắc" (màu gray) thành nút phụ, "Tiếp tục" thành nút chính, bảng dùng màu kẻ của thiết kế.
 */
const FIFO_THEME: MantineThemeOverride = {
  components: {
    Button: Button.extend({
      vars: (_theme, props) => ({ root: props.color === "gray" ? SECONDARY_BUTTON : PRIMARY_BUTTON }),
    }),
    Table: Table.extend({
      vars: () => ({ table: { "--table-border-color": "#ECEEE9", "--table-striped-color": "#F6F7F4" } }),
    }),
  },
};

/** Cảnh báo FIFO. Trả về true nếu người dùng chọn tiếp tục */
export const confirmFifo = (data: any[]): Promise<boolean> =>
  new Promise((resolve) => {
    let answered = false;
    const answer = (value: boolean) => {
      answered = true;
      modals.close(FIFO_MODAL_ID);
      resolve(value);
    };
    modals.open({
      modalId: FIFO_MODAL_ID,
      ...DIALOG_MODAL_PROPS,
      size: "xl",
      yOffset: "10dvh",
      closeOnClickOutside: false,
      // Đóng bằng Esc coi như quay lại cân nhắc, để lượt lưu kết thúc thay vì treo chờ
      onClose: () => {
        if (!answered) resolve(false);
      },
      children: (
        <div>
          <DialogHeader
            icon={<AlertIcon />}
            iconBackground="#FFF1DC"
            title="Cảnh báo Nhập trước Xuất trước (Cảnh báo FIFO)"
          />
          <div style={{ padding: "0 24px 24px" }}>
            <MantineThemeProvider theme={FIFO_THEME}>
              <FifoWarningModal data={data} onConfirm={() => answer(true)} onCancel={() => answer(false)} />
            </MantineThemeProvider>
          </div>
        </div>
      ),
    });
  });
